import { GasProductItem } from "./components/GasProductItem";
import { isMeteredCustomerType, isMonthlyCustomerType } from "@utils/func/billingCustomerType";
import { GasPriceRange } from "@utils/func/gasPriceRange";
import { PageLoading } from "@components/loading/PageLoading";
import { Stack, XStack, Circle, Text, YStack, Button, ScrollView } from "tamagui";
import { colors } from "@styles/colors";
import { RiShoppingBag3Line, RiArrowDropRightLine, RiInbox2Line, RiRestTimeLine, RiHistoryLine } from "react-icons/ri";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useEffect, useState } from "react";
import api, { handleRequest } from "@services/axios/apiManager";
import { AxiosError, AxiosResponse } from "axios";
import { useOrder, DeliverySlot } from "@utils/store/cart/useOrder";
import type { DeliveryArea } from "@components/address/func/isAddressDeliverable";
import { useUser, IdentityContext, Address } from "@utils/store/user/useUser";
import { useKiosk } from "@screen/Kiosk/useKiosk";
import SupplierSelectSheet from "@screen/Kiosk/components/SupplierSelectSheet";
import { buildXContextHeader } from "@utils/api/headers";
import { getDraftErrorMessage, isDraftAddressMismatch } from "@utils/api/draftErrorMessage";
import { isOrderableGasType } from "@utils/func/gasTitle";
import { canEnterOrder, hasReservableSlots } from "@utils/func/deliverySlots";
import { normalizeTaxId, normalizePhone } from "@utils/func/supplierIdentity";
import toast from "react-hot-toast";
import { track } from "@lib/tracker";
import { SkinCard, SkinBottomBar, SkinDivider, isGasSkinV2, v2 } from "./skin";

export interface GasCylinder {
  gas_id: number;
  price_id: number;
  gas_type: string;
  kilogram: number;
  unit_price: number;
  type: string;
  /**
   * 平台後台設定的參考售價區間（元／桶），未設定或後端判定資料不合法時為 null。
   * 純顯示用，不參與計價（見 formatGasPriceRange）。
   */
  price_range?: GasPriceRange | null;
}

export interface Commodity {
  commodity_id: number;
  price_id: number;
  commodity_type: string;
  display_name: string;
  unit_price: number;
  type: string;
}

export interface LastOrderGasItem {
  type: string;
  price_id: number;
  gas_id: number;
  gas_type: string;
  kilogram: number;
  unit_price: number;
  qty: number;
  install_location: {
    customer_delivery_id: number;
    usage_location: string;
    note: string;
    floor: number;
    is_elevator: boolean;
  } | null;
}

export interface LastOrder {
  gas_list: LastOrderGasItem[];
}

export interface SupplierData {
  supplier_id: string;
  /** 瓦斯行名稱（bootstrap available_suppliers[] 已提供）。 */
  supplier_name?: string;
  /**
   * 瓦斯行統一編號（賣方身分，下單頁需顯示）。
   * TODO(blocked: lpg-app-api bootstrap)：後端需於 available_suppliers[] 帶入
   *   suppliers.tax_id_number（Supplier 實體已有此欄，目前僅瓦斯行後台 GET /supplier 回傳）。
   * 未提供 / 非 8 碼數字時前端整段隱藏，不顯示「統編：--」。
   */
  tax_id_number?: string | null;
  /** 瓦斯行聯絡電話（bootstrap available_suppliers[] 已提供）。缺漏時不顯示撥打鍵。 */
  phone?: string | null;
  gas_cylinders: GasCylinder[];
  commodities: Commodity[];
  last_order: LastOrder | null;
  delivery_slots: DeliverySlot[];
  is_open_now: boolean;
  /**
   * 下一個營業時段的開始時間（ISO，+08:00）。非營業時「立即配送」提示文字用。
   * 後端舊版未提供（undefined）或找不到時視為 null →「現在休息中，請稍後再點選」。
   */
  next_open_at?: string | null;
  /**
   * 客戶目前「押桶」（cis_cylinder_mortgage，take_cylinder_type=押桶 net > 0）的公斤數清單。
   * 只顯示客戶有押桶紀錄的公斤數瓶身。
   * 後端 bootstrap 尚未提供此欄位時為 undefined → 不過濾（顯示全部，維持現狀）。
   * TODO(blocked: lpg-app-api bootstrap)：後端需於 available_suppliers[] 帶入此欄位，
   *   SQL 概念：SELECT cylinder_specification FROM cis_cylinder_mortgage
   *   WHERE cis_id = ANY(cisIds) GROUP BY cylinder_specification
   *   HAVING SUM(CASE WHEN take_cylinder_type='押桶' THEN numbers_of_cylinder
   *                   WHEN take_cylinder_type='還桶' THEN -numbers_of_cylinder ELSE 0 END) > 0
   */
  mortgage_kilograms?: number[] | null;
  /**
   * 平台層級設定（PW 後台控制）。目前含 show_invoice：訂購流程是否顯示發票填寫區。
   * 三個平台開關一律 opt-in：後端未提供（undefined）時前端視為關閉，需 PW 後台明確開啟。
   */
  platform_setting?: { show_invoice?: boolean; enable_electronic_payment?: boolean } | null;
  /**
   * 此瓦斯行是否開放「線上預約配送」（supplier.system_setting.allow_customer_reservation）。
   * 後端未提供時為 undefined → 視為未開放（=== true 才開放，與後端下單端 enforcement 對齊）。
   * 未開放時：不顯示預約選項；若當日公休又未開放預約 → 完全不可下單（尚未營業）。
   */
  allow_customer_reservation?: boolean;
  /**
   * 此瓦斯行是否隱藏金額（supplier.system_setting.hide_amount_on_pending）。=== true 才隱藏。
   * 開啟時整個下訂流程不顯示價格（客戶照常下單，金額由瓦斯行確認）；訂單頁另在待確認時隱藏金額。
   */
  hide_amount_on_pending?: boolean;
  /**
   * 此 supplier 下客戶的 cis.customer_type（如「月結客戶」）。
   * 用於把付款方式由「貨到付款」改顯示為「月結」。後端未提供時為 undefined / null。
   */
  customer_type?: string | null;
  /**
   * 此瓦斯行的配送範圍（縣市 → 全區 or 指定鄉鎮市區）。
   * 新增地址時用來即時提示所選 city/area 是否可配送（見 isAddressDeliverable）。
   * 後端未提供或空陣列 → 視為不限制配送範圍（全部可送）。
   */
  delivery_areas?: DeliveryArea[];
}

/**
 * 月結客戶常數搬到 @utils/func/billingCustomerType（與走表客戶的判斷放在一起，
 * 兩者都對應後端 deriveBillingMethod 的分支）。這裡 re-export 維持既有匯入不變。
 */
export { MONTHLY_CUSTOMER_TYPE } from "@utils/func/billingCustomerType";

/**
 * 依客戶押桶紀錄過濾可訂購的瓦斯瓶身：只保留客戶有押桶（公斤數）的瓶身。
 * mortgageKilograms 為 undefined/null（後端尚未提供）時回傳原清單，避免擋下單。
 */
export function filterGasByMortgage(gas: GasCylinder[], mortgageKilograms?: number[] | null): GasCylinder[] {
  if (mortgageKilograms == null) return gas;
  const allowed = new Set(mortgageKilograms);
  return gas.filter((cylinder) => allowed.has(cylinder.kilogram));
}

// 本 session 是否已從 last_order 自動帶入過購物車。
// 用 sessionStorage 而非 component state：products 頁會在 cart 清空後重新 mount，
// component state 會被重置；sessionStorage 跨 mount/route 持久，瀏覽器分頁關閉後清除。
// cart.tsx 在送單成功後會清掉此 flag，讓下次回到 products 可用新的 last_order 重新帶入；
// 手動砍光則不清，避免使用者明確清空後又被「helpfully」重建。
export const AUTO_POPULATED_FLAG = "gasShop_autoPopulatedFromLastOrder";

/**
 * 反查 last_order 安裝位所屬的「地址」：用 install_location.customer_delivery_id 比對各地址的
 * install_locations，找出真正擁有它的地址。
 * 用途：auto-populate / 恢復上一筆時，草稿要建在上一筆訂單「自己的地址」，而非當下選的地址，
 * 否則 customer_delivery_id 不屬於目前地址會被 BE 擋（400）並導致安裝位對不上。
 * 查不到（後端未帶 install_locations，或安裝位屬已刪地址）回 null → 呼叫端 fallback 當前地址。
 */
const resolveLastOrderAddress = (lastOrder: LastOrder, addresses: Address[]): Address | null => {
  const deliveryIds = new Set(
    (lastOrder?.gas_list || [])
      .map((it) => it.install_location?.customer_delivery_id)
      .filter((id): id is number => id != null),
  );
  if (deliveryIds.size === 0) return null;
  return addresses.find((addr) => (addr.install_locations || []).some((loc) => deliveryIds.has(loc.customer_delivery_id))) ?? null;
};

export const GasShopProducts = () => {
  const navigation = useNavigate();
  const [searchParams] = useSearchParams();
  const supplierId = searchParams.get("supplierId");
  const { cusJwtToken } = useCusAuth();
  const {
    productList,
    setDeliverySlots,
    setIsOpenNow,
    setNextOpenAt,
    setAllowReservation,
    allowReservation,
    setHideAmountOnPending,
    setDeliveryAreas,
    setSupplierIdentity,
    setAllowElectronicPay,
    setIsMonthlyCustomer,
    setIsMeteredCustomer,
    deliverySlots,
    syncDraft,
  } = useOrder();
  const { chooseIdentity, setChooseIdentity } = useUser();
  const { needSupplierSelect, setNeedSupplierSelect } = useKiosk(); // 新客戶/未匹配 → 下單前先選瓦斯行
  const primarySupplierId = supplierId || chooseIdentity?.primary_supplier?.supplier_id;
  const [category, setCategory] = useState<"gas" | "other">("gas");
  const skinV2 = isGasSkinV2();
  // 保留本供應商的 last_order，供「恢復上一筆訂購紀錄」手動帶入（購物車清空後 auto-populate flag 已設，不會自動重帶）
  const [lastOrder, setLastOrder] = useState<LastOrder | null>(null);
  const [resProList, setResProList] = useState<{
    state: boolean;
    gas: GasCylinder[];
    other: Commodity[];
    isOpenNow: boolean;
  }>({ state: false, gas: [], other: [], isOpenNow: true }); //-- 商品列表：state(載入狀態)

  useEffect(() => {
    if (!primarySupplierId) {
      setResProList({ state: true, gas: [], other: [], isOpenNow: true });
      return;
    }

    setResProList((prev) => ({ ...prev, state: false })); //-- 開始載入（頁內 state，不拉全域遮罩）
    // fail-open：重新抓 bootstrap 期間先清空配送範圍提示，避免沿用上一個 supplier（或已過期）的範圍
    // 誤導使用者；沒有範圍資料時 isAddressDeliverable 視為全部可送，不會誤擋。
    setDeliveryAreas([]);
    handleRequest({
      asyncRequest: () =>
        api.get("/app-api/platform-order-drafts/bootstrap", {
          headers: { Authorization: `Bearer ${cusJwtToken}`, "X-Context": buildXContextHeader(chooseIdentity) },
        }),
      successCallback: (res: AxiosResponse) => {
        const { data } = res.data;
        const targetData = data?.available_suppliers.find((item: SupplierData) => item.supplier_id === primarySupplierId);

        // 保留 last_order 供手動「恢復上一筆訂購紀錄」使用
        setLastOrder(targetData?.last_order ?? null);

        // 賣方身分（版頭顯示「這單跟誰買」）：bootstrap 為主，缺欄位時退回身分的 primary_supplier。
        // 名稱是必要條件——沒有名稱就整段不顯示，只有統編/電話沒有意義。
        const identityName = (targetData?.supplier_name || chooseIdentity?.primary_supplier?.supplier_name || "").trim();
        setSupplierIdentity(
          identityName
            ? {
                name: identityName,
                taxId: normalizeTaxId(targetData?.tax_id_number ?? chooseIdentity?.primary_supplier?.tax_id_number),
                phone: normalizePhone(targetData?.phone ?? chooseIdentity?.primary_supplier?.phone),
              }
            : null,
        );

        setResProList({
          state: true,
          // 先濾掉非瓦斯氣別（丙烷／純丙烷），再依押桶紀錄過濾公斤數
          //（後端尚未帶 mortgage_kilograms 時顯示全部）
          gas: filterGasByMortgage(
            (targetData?.gas_cylinders || []).filter((c: GasCylinder) => isOrderableGasType(c.gas_type)),
            targetData?.mortgage_kilograms,
          ),
          other: targetData?.commodities || [],
          isOpenNow: targetData?.is_open_now ?? true,
        });
        // 同步到 store，讓配送時段選擇器（DeliverTimeCard）能在公休時隱藏「立即配送」
        setIsOpenNow(targetData?.is_open_now ?? true);
        // 下一個營業時段開始時間（非營業時「立即配送」提示用）；後端未提供時視為 null
        setNextOpenAt(targetData?.next_open_at ?? null);
        // 同步「預約配送開關」：=== true 才開放（與後端 enforcement 對齊；未提供時視為未開放）
        setAllowReservation(targetData?.allow_customer_reservation === true);
        // 同步「隱藏金額開關」：=== true 才隱藏（未提供時視為不隱藏，維持顯示價格）
        setHideAmountOnPending(targetData?.hide_amount_on_pending === true);
        // 同步「配送範圍」：供新增地址時即時提示所選 city/area 是否可配送（未提供時為空 → 全部可送）
        setDeliveryAreas(targetData?.delivery_areas ?? []);
        // 同步「電子付款開關」：=== true 才開（配送時間之後多一步「付款方式」）；月結客戶該步現金選項顯示「月結」
        setAllowElectronicPay(targetData?.platform_setting?.enable_electronic_payment === true);
        // 月結與走表客戶依計費方式結算，下單當下金額未定 → 不顯示付款方式那一步
        setIsMonthlyCustomer(isMonthlyCustomerType(targetData?.customer_type));
        setIsMeteredCustomer(isMeteredCustomerType(targetData?.customer_type));

        // 如果購物車為空，且有最後一筆訂單，則自動 POST 一筆 draft 並 syncDraft 帶入購物車
        // flag 在呼叫前 set，避免 POST 失敗時下次 mount 又重試造成 retry loop
        const isAutoPopulated = sessionStorage.getItem(AUTO_POPULATED_FLAG);
        const willAutoPopulate =
          !isAutoPopulated &&
          productList?.length === 0 &&
          !!targetData?.last_order?.gas_list &&
          targetData.last_order.gas_list.length > 0;

        // Option A：草稿建在 last_order「自己的地址」（用 customer_delivery_id 反查），而非當下選的地址。
        // 反查不到時回 null → fallback 當前選定地址（行為同舊版）。
        const lastOrderAddress = willAutoPopulate
          ? resolveLastOrderAddress(targetData.last_order, (data?.available_addresses || []) as Address[])
          : null;

        if (willAutoPopulate) {
          const populateAddress =
            lastOrderAddress || chooseIdentity?.choose_address || (data?.available_addresses?.[0] as Address | undefined);
          if (populateAddress?.customer_address_id) {
            sessionStorage.setItem(AUTO_POPULATED_FLAG, "true");
            autoPopulateFromLastOrder(targetData.last_order, populateAddress.customer_address_id);
          }
        }

        // 確保從 primary_supplier 對應的資料中取得 delivery_slots
        const slots = targetData?.delivery_slots;
        if (slots) {
          setDeliverySlots(slots);
        }

        //-- 同步地址資訊
        if (chooseIdentity && data?.available_addresses?.length > 0) {
          const newAddresses = data.available_addresses;
          // Option A：若這次跨地址 auto-populate，配送地址一起切到 last_order 的地址（連 choose_address 一起切過去）
          const newChooseAddress = lastOrderAddress || chooseIdentity.choose_address || newAddresses[0];

          // 只有在地址清單或所選地址真的不同時才更新，避免無限 re-render
          const isAddressListSame = JSON.stringify(chooseIdentity.address) === JSON.stringify(newAddresses);
          const isChooseAddressSame = JSON.stringify(chooseIdentity.choose_address) === JSON.stringify(newChooseAddress);

          if (!isAddressListSame || !isChooseAddressSame) {
            setChooseIdentity({
              ...chooseIdentity,
              address: newAddresses,
              choose_address: newChooseAddress,
            });
          }
        }
      },
      errorCallback: (error: any) => {
        console.log("err", error);
        setResProList({ state: true, gas: [], other: [], isOpenNow: true });
        // fail-open：bootstrap 失敗時清空配送範圍，避免殘留舊資料造成錯誤提示
        setDeliveryAreas([]);
      },
    });
    // context_id：同瓦斯行多身分（2026-09-03）下，切身分（supplier 不變）也要重抓——
    // X-Context 變了，商品/地址/配送範圍都是身分維度的資料。
  }, [primarySupplierId, chooseIdentity?.context_id]);

  // 把 last_order 的 gas_list 轉成 GasItemReqDto 並 POST 建 draft。
  // - 過濾掉 install_location 為 null 的 legacy item
  // - 帶 customer_delivery_id：BE 走 upsert UPDATE 路徑，使用者之後改 usage_location 也會
  //   updated 到同一筆 row，不會建出重複的 customer_delivery（spec app-customer-delivery §4.1）
  // - 跨地址 race（last_order 來自另一地址、當下選的 address 不同）：BE 會 400「does not
  //   belong to draft customer_address_id」，catch 後 retry 一次（不帶 customer_delivery_id）
  //   讓 BE content-match upsert
  const autoPopulateFromLastOrder = (lastOrder: LastOrder, customerAddressId: string | number, isManual = false) => {
    const installLocation = (item: LastOrderGasItem) => item?.install_location ?? null;
    const mapItem = (item: LastOrderGasItem, withCustomerDeliveryId: boolean) => {
      const loc = installLocation(item)!;
      return {
        type: item.type,
        price_id: item.price_id,
        gas_id: item.gas_id,
        gas_type: item.gas_type,
        kilogram: item.kilogram,
        unit_price: item.unit_price,
        qty: item.qty,
        usage_location: loc.usage_location,
        note: loc.note,
        floor: loc.floor,
        is_elevator: loc.is_elevator,
        ...(withCustomerDeliveryId && loc.customer_delivery_id != null ? { customer_delivery_id: loc.customer_delivery_id } : {}),
      };
    };
    const eligibleItems = (lastOrder?.gas_list || []).filter((item: LastOrderGasItem) => installLocation(item) !== null);
    if (eligibleItems.length === 0) return;

    const headers = { Authorization: `Bearer ${cusJwtToken}`, "X-Context": buildXContextHeader(chooseIdentity) };
    const postOnce = (withId: boolean) =>
      api.post(
        "/app-api/platform-order-drafts",
        {
          supplier_id: primarySupplierId,
          customer_address_id: customerAddressId,
          gas_list: eligibleItems.map((item) => mapItem(item, withId)),
        },
        { headers },
      );

    // 帶入成功後提示使用者：避免他們以為是自己加的，或漏看已帶入的內容（一鍵下單已有類似提示）
    const notifyPrefilled = () =>
      toast.success(isManual ? "已帶入上一筆訂單內容，請確認後再送出" : "已自動帶入上一筆訂單內容，請確認後再送出", {
        id: "auto-populated-last-order",
        duration: 3500,
      });

    // 「成功」必須是 draft 真的帶入了品項，只看 draft 是否 truthy 會誤判成功
    //（既有 draft 時 POST /drafts 會回 200 但 gas_list 為空）。
    const draftHasItems = (draft: { gas_list?: unknown[]; commodity_list?: unknown[] } | null | undefined) =>
      ((draft?.gas_list?.length ?? 0) + (draft?.commodity_list?.length ?? 0)) > 0;

    const applyDraft = (draft: unknown) => {
      syncDraft(draft);
      notifyPrefilled();
    };

    // 失敗只在手動帶入時提示使用者（自動帶入保持安靜，避免初次進頁就跳錯誤）
    const notifyFailed = (context: string, detail: unknown) => {
      console.warn(`autoPopulateFromLastOrder ${context}`, detail);
      // detail 為 AxiosError 時帶出後端真正原因，否則 fallback 到帶入專屬訊息
      if (isManual) toast.error(getDraftErrorMessage(detail, "未能帶入上一筆訂單，請手動加入瓦斯"));
    };

    // 跨地址 race：customer_delivery_id 屬於別的地址時 BE 會 400，要退回不帶 id 重試讓 BE content-match
    //（判定邏輯共用 isDraftAddressMismatch，與 item-level add/update 一致）
    const isAddressMismatch = isDraftAddressMismatch;

    // BE gas-items 的 dedup key = (price_id, customer_delivery_id)。restore 要冪等：
    // 已在 draft 的品項就跳過，否則重複加會 409 GAS_ITEM_DUPLICATE_LINE。
    const gasDedupKey = (priceId: unknown, cdId: unknown) => `${priceId}|${cdId ?? "null"}`;
    const isDuplicateLine = (err: unknown) =>
      (err as AxiosError<{ code?: string }>)?.response?.data?.code === "GAS_ITEM_DUPLICATE_LINE";

    // 逐筆把瓦斯加入「既有 draft」：POST /platform-order-drafts 對既有 draft 不會 merge gas_list
    //（只回傳既有 draft），必須走 item-level endpoint。回傳最後一次的 draft。
    const addItemToDraft = async (draftId: string, item: LastOrderGasItem) => {
      const url = `/app-api/platform-order-drafts/${draftId}/gas-items`;
      try {
        const res = await api.post(url, mapItem(item, true), { headers });
        return res?.data?.data?.draft;
      } catch (err) {
        if (isDuplicateLine(err)) return undefined; // 已存在（race）→ 視為跳過，不中斷整批
        if (!isAddressMismatch(err)) throw err;
        const res = await api.post(url, mapItem(item, false), { headers });
        return res?.data?.data?.draft;
      }
    };

    // 只加「draft 還沒有的」品項。全部都已存在 → 回 null，讓上層改用既有 draft（自我修復不同步）。
    const addMissingItemsToDraft = async (draftId: string, existingGasList: Array<{ price_id?: unknown; customer_delivery_id?: unknown }> = []) => {
      const seenKeys = new Set((existingGasList || []).map((g) => gasDedupKey(g.price_id, g.customer_delivery_id)));
      let latest: { gas_list?: unknown[]; commodity_list?: unknown[] } | undefined;
      let addedAny = false;
      for (const item of eligibleItems) {
        const key = gasDedupKey(item.price_id, installLocation(item)?.customer_delivery_id ?? null);
        if (seenKeys.has(key)) continue; // 已在 draft → 跳過
        const draft = await addItemToDraft(draftId, item);
        seenKeys.add(key);
        if (draft) {
          latest = draft;
          addedAny = true;
        }
      }
      return addedAny ? latest : null;
    };

    void (async () => {
      try {
        // 1) 是否已有「同地址」的 draft？有的話 POST /drafts 不會帶入品項，必須用 item-level endpoint 逐筆加入
        let existingDraftId: string | null = null;
        let existingDraft: { gas_list?: Array<{ price_id?: unknown; customer_delivery_id?: unknown }> } | null = null;
        try {
          const cur = await api.get("/app-api/platform-order-drafts/current", { headers });
          const curDraft = cur?.data?.data?.draft;
          if (curDraft?.draft_id && String(curDraft.customer_address_id) === String(customerAddressId)) {
            existingDraftId = curDraft.draft_id;
            existingDraft = curDraft;
          }
        } catch {
          // 取不到 current 視為無 draft，往下走建立流程
        }

        if (existingDraftId) {
          // 只補 draft 還沒有的品項；全都已存在就直接同步既有 draft（購物車與後端對齊、按鈕消失，不報重複）
          const added = await addMissingItemsToDraft(existingDraftId, existingDraft?.gas_list);
          const finalDraft = added ?? existingDraft;
          if (draftHasItems(finalDraft)) return applyDraft(finalDraft);
          return notifyFailed("加入既有 draft 後仍無品項", { existingDraftId, finalDraft });
        }

        // 2) 無同地址 draft → POST /drafts 帶 gas_list 直接建立（無 draft 時會含品項）
        let createRes;
        try {
          createRes = await postOnce(true);
        } catch (err) {
          if (!isAddressMismatch(err)) return notifyFailed("建立 draft 失敗", err);
          createRes = await postOnce(false);
        }
        const createdDraft = createRes?.data?.data?.draft;
        if (draftHasItems(createdDraft)) return applyDraft(createdDraft);

        // 3) 保險：POST 回了 draft_id 卻沒帶品項（例如後端剛好已有 draft）→ 改走 item-level 補齊
        const createdDraftId = createdDraft?.draft_id ?? createRes?.data?.data?.draft_id ?? null;
        if (createdDraftId) {
          const added = await addMissingItemsToDraft(createdDraftId, createdDraft?.gas_list);
          const finalDraft = added ?? createdDraft;
          if (draftHasItems(finalDraft)) return applyDraft(finalDraft);
        }
        notifyFailed("建立 draft 後仍無品項", { createdDraft });
      } catch (err) {
        notifyFailed("帶入失敗", err);
      }
    })();
  };

  // last_order 中可帶入的瓦斯數量（須有安裝位置，與 autoPopulate 的過濾條件一致）
  const lastOrderEligibleCount = (lastOrder?.gas_list || []).filter((item) => item?.install_location != null).length;

  // 手動「恢復上一筆訂購紀錄」：帶入上一筆的瓦斯與安裝位置（沿用 autoPopulate 的 upsert/retry 邏輯）
  // Option A：草稿建在 last_order 自己的地址（反查 customer_delivery_id），並把 choose_address 一起切過去。
  const handleRestoreLastOrder = () => {
    if (!lastOrder) return;
    track("entry_click", { entry_source: "restore_last_order" });
    const addressList = Array.isArray(chooseIdentity?.address) ? (chooseIdentity?.address as Address[]) : [];
    const lastOrderAddress = resolveLastOrderAddress(lastOrder, addressList);
    const targetAddress = lastOrderAddress || chooseIdentity?.choose_address;
    if (!targetAddress?.customer_address_id) {
      toast.error("尚未選擇配送地址");
      return;
    }
    if (
      lastOrderAddress &&
      chooseIdentity &&
      String(lastOrderAddress.customer_address_id) !== String(chooseIdentity.choose_address?.customer_address_id)
    ) {
      setChooseIdentity({ ...chooseIdentity, choose_address: lastOrderAddress });
    }
    autoPopulateFromLastOrder(lastOrder, targetAddress.customer_address_id, true);
  };

  // V2：瓦斯依公斤數由大到小排（後端回傳順序不固定）。V1 維持原順序不動。
  const displayList =
    skinV2 && category === "gas"
      ? [...resProList.gas].sort((a, b) => b.kilogram - a.kilogram)
      : category === "gas"
        ? resProList.gas
        : resProList.other;

  // 可預約 = 瓦斯行開放線上預約(allowReservation) 且 有可預約時段。未開放預約時 reservable 一律 false。
  // 公休但仍可預約時照常開放下單（只能走預約）；公休又未開放預約 → 完全不可下單（尚未營業）。
  // 公休又開放預約但沒有任何可預約時段 → 依 CLOSED_NO_SLOT_BEHAVIOR（與 Kiosk 共用）決定擋或進。
  const reservable = allowReservation && hasReservableSlots(deliverySlots);
  const canOrder = canEnterOrder({ isOpenNow: resProList.isOpenNow, allowReservation, slots: deliverySlots });
  const closedButReservable = !resProList.isOpenNow && reservable;

  // 商品清單載入完成、且判定為「尚未營業（不可下單）」時追蹤一次；resProList.state 確保不會在
  // 載入中的預設值（isOpenNow: true）誤觸發
  useEffect(() => {
    if (resProList.state && !canOrder) {
      track("order_blocked_not_open", { surface_context: "gasshop" });
    }
  }, [resProList.state, canOrder]);

  return (
    <YStack flex={1} justifyContent="space-between">
      {!canOrder ? (
        <YStack py="$10" alignItems="center" flex={1} justifyContent="center">
          <RiRestTimeLine fontSize={30} color={colors.gray} />
          <Text mt="$2" fontWeight="bold" fontSize={18} color={colors.gray}>
            尚未營業
          </Text>
        </YStack>
      ) : (
        <>
          <YStack>
            {closedButReservable && (
              <XStack
                ai="center"
                gap={9}
                mb="$3"
                px={14}
                py={12}
                br={12}
                backgroundColor="#FFF7E6"
                borderWidth={1}
                borderColor="#FFD591">
                <RiRestTimeLine size={18} color="#D48806" />
                <Text fontSize={13} fontWeight="700" color="#AD6800" flex={1}>
                  今日公休，可預約其他日期配送
                </Text>
              </XStack>
            )}
            {/* 購物車為空且有上一筆可帶入訂單時，提供手動「恢復上一筆訂購紀錄」 */}
            {productList?.length === 0 &&
              lastOrderEligibleCount > 0 &&
              (skinV2 ? (
                /* V2：圓角 20、無粗框、圖示收進白色圓片，與卡片／膠囊鈕同一套語彙。
                   原本的 1.5px 主色框在這套設計裡太搶，會比下面的商品卡還重。 */
                <XStack
                  onPress={handleRestoreLastOrder}
                  mb="$3"
                  p={14}
                  gap={12}
                  borderRadius={20}
                  backgroundColor={v2.primarySoft}
                  borderWidth={1}
                  borderColor={v2.primarySoftBorder}
                  ai="center"
                  jc="space-between"
                  cursor="pointer"
                  pressStyle={{ opacity: 0.7 }}>
                  <XStack ai="center" gap={12} flex={1} minWidth={0}>
                    <Circle size={36} backgroundColor="#fff" flexShrink={0}>
                      <RiHistoryLine color={colors.primary} size={19} />
                    </Circle>
                    <YStack flex={1} minWidth={0}>
                      <Text fontSize={14.5} fontWeight="700" color={v2.title}>
                        恢復上一筆訂購紀錄
                      </Text>
                      <Text fontSize={12} color={v2.sub} mt={1}>
                        帶入上次的瓦斯與安裝位置
                      </Text>
                    </YStack>
                  </XStack>
                  <Text fontSize={20} color={colors.primary} flexShrink={0}>
                    ›
                  </Text>
                </XStack>
              ) : (
                  <XStack
                    onPress={handleRestoreLastOrder}
                    mb="$3"
                    p={14}
                    br={12}
                    bg={colors.primaryActive}
                    borderColor={colors.primary}
                    borderWidth={1.5}
                    ai="center"
                    jc="space-between"
                    cursor="pointer"
                    pressStyle={{ opacity: 0.85 }}>
                    <XStack ai="center" gap={10} flex={1}>
                      <RiHistoryLine color={colors.primary} size={22} />
                      <YStack flex={1}>
                        <Text fontSize={14} fow="700" color={colors.primary}>
                          恢復上一筆訂購紀錄
                        </Text>
                        <Text fontSize={12} color={colors.gray}>
                          帶入上次的瓦斯與安裝位置
                        </Text>
                      </YStack>
                    </XStack>
                    <RiArrowDropRightLine color={colors.primary} size={24} />
                  </XStack>
              ))}
            {skinV2 ? (
              displayList.length > 0 && (
                <SkinCard>
                  {displayList.map((item, idx) => (
                    <YStack key={("gas_id" in item ? item.gas_id : item.commodity_id) || Math.random()}>
                      {idx > 0 && <SkinDivider my={12} />}
                      <GasProductItem data={item} />
                    </YStack>
                  ))}
                </SkinCard>
              )
            ) : (
              <YStack gap="$2.5" alignItems="center">
                {displayList.map((item) => {
                  return <GasProductItem key={("gas_id" in item ? item.gas_id : item.commodity_id) || Math.random()} data={item} />;
                })}
              </YStack>
            )}
            {displayList?.length === 0 &&
              (resProList.state ? (
                <YStack py="$5" alignItems="center" flex={1} justifyContent="center">
                  <RiInbox2Line fontSize={30} color={colors.gray} />
                  <Text mt="$2" fontWeight="bold" fontSize={20} color={colors.gray}>
                    尚無商品
                  </Text>
                </YStack>
              ) : (
                <PageLoading label="商品載入中" minHeight={200} />
              ))}
          </YStack>

          {productList?.length > 0 &&
            (skinV2 ? (
              <SkinBottomBar
                primaryLabel={`結帳 · ${productList.length} 項`}
                onPrimary={() => {
                  const incompleteItem = productList.find((p) => !p.usage_location);
                  if (incompleteItem) {
                    toast.error("部分商品尚未設定安裝位置");
                    return;
                  }
                  navigation("/gasShop/deliveryTime");
                }}
              />
            ) : (
                <XStack
                className="gasshop-btn-gradient"
                borderRadius={12}
                p={15}
                jc="space-between"
                ai="center"
                style={{ position: "sticky", bottom: "0", zIndex: 10 }}
                onPress={() => {
                  const incompleteItem = productList.find((p) => !p.usage_location);
                  if (incompleteItem) {
                    toast.error("部分商品尚未設定安裝位置");
                    return;
                  }
                  navigation("/gasShop/deliveryTime");
                }}>
                <XStack ai="center" gap={10}>
                  <Stack position="relative">
                    <RiShoppingBag3Line color="white" size={20} />
                    <Circle size={16} backgroundColor="white" position="absolute" t={-5} r={-5} jc="center" ai="center">
                      <Text color={colors.primary} fontSize={10} fow="700">
                        {productList?.length}
                      </Text>
                    </Circle>
                  </Stack>
                  <Text color="white" fontSize={16} fow="600">
                    結帳
                  </Text>
                </XStack>
                <XStack ai="center" gap={5}>
                  <RiArrowDropRightLine color="white" size={24} />
                </XStack>
              </XStack>
            ))}

        </>
      )}

      {/* 新客戶/未匹配到瓦斯行 → 下單前先選瓦斯行 */}
      <SupplierSelectSheet
        isOpen={needSupplierSelect}
        onPick={(id) => {
          // TODO: 綁定所選瓦斯行到帳戶（primary_supplier）；demo 先清旗標並用 supplierId 帶入
          setNeedSupplierSelect(false);
          navigation(`/gasShop/products?supplierId=${id}`);
        }}
      />

    </YStack>
  );
};

export default GasShopProducts;
